// Yearly — a calendar-year grid for one hub. Rows are the network total + each
// customer; columns are periods (weeks, months, or quarters). Week columns expand
// into a 7-day breakdown. Shows forecast and/or actuals, in packages or trucks,
// optionally filtered to one customer and/or one package type. Context rows show
// the seasonal multiplier and which periods carry hub events. A rightmost Year
// column totals each row.
//
// Pure engine computation (forecast) + customer_actuals (actuals).

import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Field, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDow, fmtMD, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Customer, CustomerLane, Hub, HubEvent, PackageMix } from "../../lib/types";
import {
  activeAllocationPct,
  dowForCustomerHub,
  forecastCustomerHub,
  growthAdjusted,
  impliedLoads,
  reroutedInForCustomerHub,
  reroutedOutForCustomerHub,
  seasonalMultiplier,
} from "../../lib/forecast/engine";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listHubs } from "../hubs/hubsApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import {
  buildOverrideMap,
  listAccountOverrides,
  type ForecastOverride,
} from "./overridesApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";

type View = "both" | "forecast" | "actuals";
type Metric = "packages" | "trucks";
type Granularity = "week" | "month" | "quarter";
type PkgType = "all" | "cartons" | "polybags" | "irregular";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface DayVals {
  fPkg: number;
  aPkg: number;
  fTrk: number;
  aTrk: number;
  rin: number;
  rout: number;
}
const ZERO: DayVals = { fPkg: 0, aPkg: 0, fTrk: 0, aTrk: 0, rin: 0, rout: 0 };

interface Period {
  key: string;
  label: string; // W12 / Jan / Q1
  sub: string; // week-ending date (week) or "" (month/quarter)
  days: string[];
  expandable: boolean; // only weeks expand to days
  current: boolean; // contains today
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-slate-300">
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-xs font-medium ${i > 0 ? "border-l border-slate-300" : ""} ${
            value === o.value ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A labeled group of toolbar controls. */
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="flex flex-wrap items-end gap-3">{children}</div>
    </div>
  );
}

const ToolbarDivider = () => (
  <div className="hidden w-px self-stretch bg-slate-200 lg:block" aria-hidden />
);

export default function YearlyPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const nowYear = Number(today().slice(0, 4));
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [hubId, setHubId] = useState("");
  const [customerId, setCustomerId] = useState(""); // "" = all customers
  const [year, setYear] = useState(nowYear);
  const [view, setView] = useState<View>("both");
  const [metric, setMetric] = useState<Metric>("packages");
  const [granularity, setGranularity] = useState<Granularity>("week");
  const [pkgType, setPkgType] = useState<PkgType>("all");
  const [hideZeros, setHideZeros] = useState(true);
  const [wow, setWow] = useState(false);
  const [varFilter, setVarFilter] = useState(false);
  const [varPct, setVarPct] = useState("10");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedWeeks, setSelectedWeeks] = useState<Set<number>>(new Set()); // empty = all weeks
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const yearOptions = [nowYear - 2, nowYear - 1, nowYear, nowYear + 1];

  // 52 Sat→Fri weeks from the week containing Jan 1.
  const weeks = useMemo(() => {
    let w = startOfWeekSat(`${year}-01-01`);
    const out: Array<{ index: number; start: string; days: string[] }> = [];
    for (let i = 0; i < 52; i++) {
      out.push({ index: i + 1, start: w, days: Array.from({ length: 7 }, (_, d) => addDays(w, d)) });
      w = addDays(w, 7);
    }
    return out;
  }, [year]);
  const allDays = useMemo(() => weeks.flatMap((w) => w.days), [weeks]);
  const rangeStart = weeks[0]?.start ?? `${year}-01-01`;
  const rangeEnd = weeks[weeks.length - 1]?.days[6] ?? `${year}-12-31`;

  const todayStr = today();
  const currentWeekStart = startOfWeekSat(todayStr);
  const currentWeekIndex = weeks.find((w) => w.start === currentWeekStart)?.index ?? -1;
  const todayMonth = Number(todayStr.slice(5, 7)) - 1;
  const sameYear = Number(todayStr.slice(0, 4)) === year;

  // Build the period columns for the chosen granularity.
  const periods = useMemo<Period[]>(() => {
    if (granularity === "week") {
      return weeks
        .filter((w) => selectedWeeks.size === 0 || selectedWeeks.has(w.index))
        .map((w) => ({
          key: `w${w.index}`,
          label: `W${w.index}`,
          sub: fmtMD(w.days[6]),
          days: w.days,
          expandable: true,
          current: w.start === currentWeekStart,
        }));
    }
    if (granularity === "month") {
      return MONTHS.map((label, m) => ({
        key: `m${m}`,
        label,
        sub: "",
        days: allDays.filter((d) => Number(d.slice(5, 7)) - 1 === m),
        expandable: false,
        current: sameYear && todayMonth === m,
      }));
    }
    return [0, 1, 2, 3].map((q) => ({
      key: `q${q}`,
      label: `Q${q + 1}`,
      sub: "",
      days: allDays.filter((d) => Math.floor((Number(d.slice(5, 7)) - 1) / 3) === q),
      expandable: false,
      current: sameYear && Math.floor(todayMonth / 3) === q,
    }));
  }, [granularity, weeks, allDays, currentWeekStart, todayMonth, sameYear, selectedWeeks]);

  useEffect(() => {
    if (!accountId) return;
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listAccountLanes(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountCustomerActuals(accountId, rangeStart, rangeEnd),
    ])
      .then(([c, h, ln, ov, ev, ac]) => {
        const activeHubs = h.filter((x) => x.active);
        setCustomers(c);
        setHubs(activeHubs);
        setLanes(ln);
        setOverrides(ov);
        setEvents(ev);
        setActuals(ac);
        setHubId((prev) => prev || activeHubs[0]?.id || "");
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load yearly data.")));
  }, [accountId, rangeStart, rangeEnd]);

  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId: cid, lane } of lanes) m.set(`${cid}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);
  const mixByKey = useMemo(() => {
    const m = new Map<string, PackageMix>();
    for (const { customerId: cid, lane } of lanes) m.set(`${cid}|${lane.hubId}`, lane.packageMix);
    return m;
  }, [lanes]);

  // Fraction of a lane's volume in the selected package type (1 for "all").
  const mixFrac = (cid: string): number => {
    if (pkgType === "all") return 1;
    const mix = mixByKey.get(`${cid}|${hubId}`);
    const total = mix ? mix.cartons + mix.polybags + mix.irregular : 0;
    if (!mix || total <= 0) return 0; // mix unspecified → can't attribute to a type
    return (mix[pkgType] ?? 0) / 100;
  };

  // When one customer is selected, its per-hub DOW factors can be shown as a row.
  const selectedCustomer = customerId ? customers.find((c) => c.id === customerId) ?? null : null;

  const hubCustomers = useMemo(
    () =>
      customers
        .filter(
          (c) =>
            c.active &&
            c.allocations.some((a) => a.hubId === hubId) &&
            (!customerId || c.id === customerId)
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [customers, hubId, customerId]
  );

  const perCustomer = useMemo(() => {
    if (!hubId) return [];
    const actualMap = new Map<string, CustomerActual>();
    for (const a of actuals) {
      if (a.hubId !== hubId) continue;
      actualMap.set(`${a.customerId}|${a.date}`, a);
    }
    return hubCustomers.map((c) => {
      const cap = capByKey.get(`${c.id}|${hubId}`);
      const frac = mixFrac(c.id);
      const byDate = new Map<string, DayVals>();
      for (const d of allDays) {
        const fPkg = forecastCustomerHub(c, hubId, d, overrideMap, events) * frac;
        // Actuals use their real package-type split when present; else fall back to lane mix.
        const act = actualMap.get(`${c.id}|${d}`);
        let aPkg = 0;
        if (act) {
          if (pkgType === "all") aPkg = act.actualVolume;
          else {
            const bd = act[pkgType as "cartons" | "polybags" | "irregular"];
            aPkg = bd != null ? bd : act.actualVolume * frac;
          }
        }
        const rin = (events.length ? reroutedInForCustomerHub(c, hubId, d, events) : 0) * frac;
        const rout = (events.length ? reroutedOutForCustomerHub(c, hubId, d, events) : 0) * frac;
        if (fPkg === 0 && aPkg === 0 && rin === 0 && rout === 0) continue;
        byDate.set(d, {
          fPkg,
          aPkg,
          fTrk: impliedLoads(fPkg, cap),
          aTrk: impliedLoads(aPkg, cap),
          rin,
          rout,
        });
      }
      return { id: c.id, name: c.name, byDate };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId, hubCustomers, allDays, capByKey, mixByKey, overrideMap, events, actuals, pkgType]);

  const fVal = (v: DayVals) => (metric === "packages" ? v.fPkg : v.fTrk);
  const aVal = (v: DayVals) => (metric === "packages" ? v.aPkg : v.aTrk);

  function sumDays(byDate: Map<string, DayVals>, dates: string[]) {
    let f = 0;
    let a = 0;
    let rin = 0;
    let rout = 0;
    for (const d of dates) {
      const v = byDate.get(d);
      if (!v) continue;
      f += fVal(v);
      a += aVal(v);
      rin += v.rin;
      rout += v.rout;
    }
    return { f, a, rin, rout };
  }

  const rows = useMemo(() => {
    return perCustomer.filter((c) => {
      if (!hideZeros) return true;
      const { f, a } = sumDays(c.byDate, allDays);
      if (view === "forecast") return f !== 0;
      if (view === "actuals") return a !== 0;
      return f !== 0 || a !== 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCustomer, hideZeros, view, metric, allDays]);

  const totalByDate = useMemo(() => {
    const m = new Map<string, DayVals>();
    for (const c of rows) {
      for (const [d, v] of c.byDate) {
        const e = m.get(d) ?? { ...ZERO };
        e.fPkg += v.fPkg;
        e.aPkg += v.aPkg;
        e.fTrk += v.fTrk;
        e.aTrk += v.aTrk;
        e.rin += v.rin;
        e.rout += v.rout;
        m.set(d, e);
      }
    }
    return m;
  }, [rows]);

  const seriesOf = (s: { f: number; a: number }) => (view === "actuals" ? s.a : s.f);

  // Per-period {f,a} for the total + each customer (for WoW + var filter).
  const totalPeriod = useMemo(
    () => periods.map((p) => sumDays(totalByDate, p.days)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periods, totalByDate, metric]
  );
  const rowPeriod = useMemo(() => {
    const m = new Map<string, Array<{ f: number; a: number }>>();
    for (const c of rows) m.set(c.id, periods.map((p) => sumDays(c.byDate, p.days)));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, periods, metric]);
  const periodIdx = useMemo(() => new Map(periods.map((p, i) => [p.key, i])), [periods]);

  const thr = Math.max(0, Number(varPct) || 0) / 100;
  const visiblePeriods = useMemo(() => {
    if (!varFilter) return periods;
    return periods.filter((p) => {
      const s = totalPeriod[periodIdx.get(p.key) ?? 0];
      return s.a > 0 && Math.abs(s.a - s.f) / Math.max(s.f, 1) >= thr;
    });
  }, [periods, varFilter, totalPeriod, periodIdx, thr]);

  function wowDelta(arr: Array<{ f: number; a: number }>, i: number): number | null {
    if (i < 1 || !arr[i - 1] || !arr[i]) return null;
    const prev = seriesOf(arr[i - 1]);
    const cur = seriesOf(arr[i]);
    if (prev <= 0) return null;
    return ((cur - prev) / prev) * 100;
  }
  const wowEl = (d: number | null) =>
    d == null ? null : (
      <div
        className={`text-[9px] font-normal ${
          d > 0 ? "text-emerald-600" : d < 0 ? "text-red-600" : "text-slate-400"
        }`}
      >
        {d > 0 ? "▲" : d < 0 ? "▼" : ""}
        {Math.abs(d).toFixed(0)}%
      </div>
    );

  function togglePeriod(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  const expandAll = () => setExpanded(new Set(periods.filter((p) => p.expandable).map((p) => p.key)));
  const collapseAll = () => setExpanded(new Set());

  const d0 = (n: number) => (n === 0 ? "–" : fmtN(n));

  const rerouteMark = (rin: number, rout: number) => {
    if (view === "actuals" || (rin <= 0 && rout <= 0)) return null;
    const parts: string[] = [];
    if (rin > 0) parts.push(`+${fmtN(rin)} rerouted in`);
    if (rout > 0) parts.push(`−${fmtN(rout)} rerouted out`);
    return (
      <span title={`Reroute: ${parts.join(", ")}`} className="mr-0.5 align-top text-amber-600">
        ↻
      </span>
    );
  };

  const hubCode = (id: string) => hubs.find((h) => h.id === id)?.code ?? "?";
  // Hub events overlapping a set of days that affect THIS hub — either an event
  // at this hub, or a reroute elsewhere that redistributes volume into this hub.
  const eventsIn = (days: string[]): HubEvent[] => {
    if (!events.length || days.length === 0) return [];
    const lo = days[0];
    const hi = days[days.length - 1];
    return events.filter(
      (e) =>
        e.active !== false &&
        e.startDate <= hi &&
        e.endDate >= lo &&
        (e.hubId === hubId ||
          (e.redistributeVolume && (e.redistributeHubs ?? []).some((s) => s.hubId === hubId)))
    );
  };
  const seasonalAvg = (days: string[]) =>
    days.length ? days.reduce((s, d) => s + seasonalMultiplier(d), 0) / days.length : 1;

  function exportCsv() {
    const head = ["Customer", "Period", "Forecast (pkg)", "Actual (pkg)", "Forecast (trucks)", "Actual (trucks)"];
    const lines = [head.join(",")];
    const emit = (name: string, byDate: Map<string, DayVals>) => {
      for (const p of periods) {
        let fp = 0;
        let ap = 0;
        let ft = 0;
        let at = 0;
        for (const day of p.days) {
          const v = byDate.get(day);
          if (!v) continue;
          fp += v.fPkg;
          ap += v.aPkg;
          ft += v.fTrk;
          at += v.aTrk;
        }
        if (fp === 0 && ap === 0 && ft === 0 && at === 0) continue;
        lines.push([`"${name.replace(/"/g, '""')}"`, p.label, fp, ap, ft, at].join(","));
      }
    };
    emit("TOTAL", totalByDate);
    for (const c of rows) emit(c.name, c.byDate);
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `yearly_${hubs.find((h) => h.id === hubId)?.code ?? "hub"}_${year}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  function valueCell(f: number, a: number, key: string, strong = false, hi = false, rin = 0, rout = 0) {
    const base = `px-1 py-1 text-right text-[11px] tabular-nums ${strong ? "font-semibold" : ""} ${
      hi ? "bg-brand-50" : ""
    }`;
    const mark = rerouteMark(rin, rout);
    if (view === "forecast") {
      return (
        <td key={key} className={`${base} text-slate-700`}>
          {mark}
          {d0(f)}
        </td>
      );
    }
    if (view === "actuals") {
      return (
        <td key={key} className={`${base} text-slate-900`}>
          {d0(a)}
        </td>
      );
    }
    return (
      <td key={key} className={`${base} leading-tight`}>
        <div className="text-slate-500">
          {mark}
          {d0(f)}
        </div>
        <div className="text-slate-900">{d0(a)}</div>
      </td>
    );
  }

  // The subtotal/total content for a {f,a} value, honoring the view + reroute mark.
  function subContent(f: number, a: number, rin: number, rout: number, actualClass = "text-slate-900") {
    const mark = rerouteMark(rin, rout);
    if (view === "both") {
      return (
        <div className="leading-tight">
          <div className="text-slate-500">
            {mark}
            {d0(f)}
          </div>
          <div className={actualClass}>{d0(a)}</div>
        </div>
      );
    }
    return (
      <>
        {mark}
        {d0(view === "forecast" ? f : a)}
      </>
    );
  }

  // Renders the period cells for a data row (TOTAL or a customer).
  function dataPeriodCells(
    byDate: Map<string, DayVals>,
    keyPrefix: string,
    series: Array<{ f: number; a: number }>,
    strong: boolean,
    subBg: (cur: boolean) => string,
    actualClass = "text-slate-900"
  ): ReactNode {
    return visiblePeriods.map((p) => {
      const open = p.expandable && expanded.has(p.key);
      const cells: ReactNode[] = [];
      if (open) {
        for (const day of p.days) {
          const v = byDate.get(day) ?? ZERO;
          cells.push(valueCell(fVal(v), aVal(v), `${keyPrefix}-${p.key}-${day}`, strong, p.current, v.rin, v.rout));
        }
      }
      const { f, a, rin, rout } = sumDays(byDate, p.days);
      const dlt = wow ? wowDelta(series, periodIdx.get(p.key) ?? 0) : null;
      cells.push(
        <td
          key={`${keyPrefix}-${p.key}-sub`}
          className={`border-l border-slate-200 px-2 py-1.5 text-right text-[11px] ${
            strong ? "font-semibold" : "font-medium"
          } tabular-nums ${subBg(p.current)}`}
        >
          {subContent(f, a, rin, rout, actualClass)}
          {wowEl(dlt)}
        </td>
      );
      return <Fragment key={`${keyPrefix}-${p.key}`}>{cells}</Fragment>;
    });
  }

  const colSpanPer = (p: Period) => (p.expandable && expanded.has(p.key) ? p.days.length + 1 : 1);

  // ---- Forecast-driver context rows (base × growth × alloc × DOW × seasonal) ----
  const baseEff = selectedCustomer ? selectedCustomer.dailyBase * mixFrac(selectedCustomer.id) : 0;
  const growthFac = (day: string) => {
    if (!selectedCustomer || selectedCustomer.dailyBase <= 0) return 1;
    return (
      growthAdjusted(
        selectedCustomer.dailyBase,
        selectedCustomer.growth || 0,
        day,
        selectedCustomer.onboardingDate || day
      ) / selectedCustomer.dailyBase
    );
  };
  const allocPctOf = (day: string) =>
    selectedCustomer ? activeAllocationPct(selectedCustomer.allocations, hubId, day) : 0;
  const dowFacOf = (day: string) =>
    selectedCustomer
      ? dowForCustomerHub(selectedCustomer, hubId)[new Date(day + "T12:00:00").getDay()]
      : 1;

  // A driver row: a sticky label + a value per day (when expanded) + per period.
  const driverRow = (
    key: string,
    label: string,
    title: string,
    dayVal: (day: string) => ReactNode,
    subVal: (p: Period) => ReactNode
  ) => (
    <tr key={key} className="border-b border-slate-100 bg-slate-50/60 text-[10px] text-slate-500">
      <td
        className="sticky left-0 z-10 bg-slate-100 px-3 py-1 font-medium uppercase tracking-wide"
        title={title}
      >
        {label}
      </td>
      {visiblePeriods.map((p) => {
        const open = p.expandable && expanded.has(p.key);
        const cells: ReactNode[] = [];
        if (open)
          for (const day of p.days)
            cells.push(
              <td key={`${key}-${p.key}-${day}`} className="px-1 py-1 text-right tabular-nums">
                {dayVal(day)}
              </td>
            );
        cells.push(
          <td
            key={`${key}-${p.key}-s`}
            className="border-l border-slate-200 px-2 py-1 text-right tabular-nums"
          >
            {subVal(p)}
          </td>
        );
        return <Fragment key={`${key}-${p.key}`}>{cells}</Fragment>;
      })}
      <td className="border-l-2 border-slate-300 bg-slate-50/60" />
    </tr>
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Yearly Forecast</h1>
        <p className="mt-1 text-sm text-slate-500">
          The full year by {granularity}. In week view, click a week to expand its daily breakdown.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-stretch gap-x-5 gap-y-4">
          <Section label="Scope">
            <Field label="Hub">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                {hubs.length === 0 && <option value="">No active hubs</option>}
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Customer">
              <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">All customers</option>
                {customers
                  .filter((c) => c.active && c.allocations.some((a) => a.hubId === hubId))
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Year">
              <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </Field>
          </Section>

          <ToolbarDivider />

          <Section label="Display">
            <div>
              <div className="mb-1 text-xs font-medium text-slate-600">View</div>
              <Segmented
                value={view}
                onChange={setView}
                options={[
                  { value: "both", label: "Both" },
                  { value: "forecast", label: "Forecast" },
                  { value: "actuals", label: "Actuals" },
                ]}
              />
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-slate-600">Metric</div>
              <Segmented
                value={metric}
                onChange={setMetric}
                options={[
                  { value: "packages", label: "Packages" },
                  { value: "trucks", label: "Trucks" },
                ]}
              />
            </div>
            <div>
              <div className="mb-1 text-xs font-medium text-slate-600">By</div>
              <Segmented
                value={granularity}
                onChange={setGranularity}
                options={[
                  { value: "week", label: "Week" },
                  { value: "month", label: "Month" },
                  { value: "quarter", label: "Quarter" },
                ]}
              />
            </div>
          </Section>

          <ToolbarDivider />

          <Section label="Filter">
            {granularity === "week" && (
              <div className="relative">
                <div className="mb-1 text-xs font-medium text-slate-600">Weeks</div>
                <button
                  type="button"
                  onClick={() => setWeekPickerOpen((o) => !o)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  {selectedWeeks.size === 0 ? "All weeks" : `${selectedWeeks.size} weeks`} ▾
                </button>
                {weekPickerOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setWeekPickerOpen(false)} />
                    <div className="absolute z-40 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
                      <div className="mb-2 flex gap-3 text-xs">
                        <button
                          type="button"
                          className="text-brand-600 hover:underline"
                          onClick={() => setSelectedWeeks(new Set())}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="text-brand-600 hover:underline"
                          onClick={() => {
                            const s = new Set<number>();
                            for (let i = currentWeekIndex - 4; i <= currentWeekIndex + 4; i++)
                              if (i >= 1 && i <= 52) s.add(i);
                            setSelectedWeeks(s);
                          }}
                        >
                          Current ±4
                        </button>
                      </div>
                      <div className="grid max-h-48 grid-cols-7 gap-1 overflow-y-auto">
                        {weeks.map((w) => (
                          <button
                            key={w.index}
                            type="button"
                            onClick={() =>
                              setSelectedWeeks((prev) => {
                                const n = new Set(prev);
                                if (n.has(w.index)) n.delete(w.index);
                                else n.add(w.index);
                                return n;
                              })
                            }
                            className={`rounded px-1 py-1 text-[10px] ${
                              selectedWeeks.has(w.index)
                                ? "bg-brand-600 text-white"
                                : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                            }`}
                          >
                            {w.index}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <Field label="Package type">
              <Select value={pkgType} onChange={(e) => setPkgType(e.target.value as PkgType)}>
                <option value="all">All types</option>
                <option value="cartons">Cartons</option>
                <option value="polybags">Polybags</option>
                <option value="irregular">Irregular</option>
              </Select>
            </Field>
            <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
              <input type="checkbox" checked={hideZeros} onChange={(e) => setHideZeros(e.target.checked)} />
              Hide zeros
            </label>
            <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
              <input type="checkbox" checked={wow} onChange={(e) => setWow(e.target.checked)} />
              WoW Δ
            </label>
            <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
              <input type="checkbox" checked={varFilter} onChange={(e) => setVarFilter(e.target.checked)} />
              |Var| ≥
              <input
                type="number"
                min={0}
                value={varPct}
                onChange={(e) => setVarPct(e.target.value)}
                className="w-14 rounded border border-slate-300 px-1.5 py-1 text-right text-xs outline-none focus:border-brand-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
              />
              %
            </label>
          </Section>

          <ToolbarDivider />

          <Section label="Actions">
            {granularity === "week" && (
              <>
                <Button variant="ghost" onClick={expandAll}>
                  Expand all
                </Button>
                <Button variant="ghost" onClick={collapseAll}>
                  Collapse all
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={exportCsv}>
              Export CSV
            </Button>
            <Button variant="ghost" onClick={() => window.print()}>
              Print
            </Button>
          </Section>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
        {view === "both" && (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-slate-400" /> Forecast
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-slate-900" /> Actual
            </span>
          </>
        )}
        {view !== "actuals" && (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-amber-600">↻</span> includes rerouted volume (hover for in/out)
          </span>
        )}
        {varFilter && (
          <span>
            Showing {visiblePeriods.length} {granularity}
            {visiblePeriods.length === 1 ? "" : "s"} where |actual − forecast| ≥ {varPct || 0}%.
          </span>
        )}
      </div>

      {hubId && (
        <Card className="overflow-x-auto p-0">
          {rows.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">
              No {view === "actuals" ? "actuals" : "forecast"} for {year} at this hub
              {pkgType !== "all" ? ` (${pkgType})` : ""}.
            </p>
          ) : (
            <table className="text-xs">
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                  <th
                    rowSpan={2}
                    className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left font-medium uppercase tracking-wide"
                  >
                    Row
                  </th>
                  {visiblePeriods.map((p) => (
                    <th
                      key={p.key}
                      colSpan={colSpanPer(p)}
                      onClick={() => p.expandable && togglePeriod(p.key)}
                      className={`border-l border-slate-200 px-2 py-1 text-center font-medium ${
                        p.expandable ? "cursor-pointer" : ""
                      } ${p.current ? "bg-brand-100 text-brand-800" : "hover:bg-slate-50"}`}
                      title={p.sub ? `ending ${p.sub}` : p.label}
                    >
                      {p.expandable ? (expanded.has(p.key) ? "▾ " : "▸ ") : ""}
                      {p.label}
                      {p.current && <span className="ml-1 text-[10px] font-normal">• now</span>}
                    </th>
                  ))}
                  <th
                    rowSpan={2}
                    className="border-l-2 border-slate-300 bg-slate-50 px-3 py-1 text-right font-semibold uppercase tracking-wide"
                  >
                    {year}
                  </th>
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                  {visiblePeriods.map((p) => {
                    const open = p.expandable && expanded.has(p.key);
                    if (!open) {
                      return (
                        <th
                          key={p.key}
                          className={`border-l border-slate-200 px-2 py-1 text-right font-normal ${
                            p.current ? "bg-brand-50 text-brand-700" : ""
                          }`}
                        >
                          {p.sub || " "}
                        </th>
                      );
                    }
                    return (
                      <Fragment key={p.key}>
                        {p.days.map((day, di) => (
                          <th
                            key={`${p.key}-${di}`}
                            className={`px-1 py-1 text-right ${di === 0 ? "border-l border-slate-200" : ""} ${
                              p.current ? "bg-brand-50 text-brand-700" : ""
                            }`}
                          >
                            {fmtDow(day)}
                          </th>
                        ))}
                        <th
                          className={`border-l border-slate-200 px-2 py-1 text-right font-semibold text-slate-500 ${
                            p.current ? "bg-brand-50" : ""
                          }`}
                        >
                          Total
                        </th>
                      </Fragment>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Forecast drivers, in formula order. Per-customer rows show only
                    when one customer is selected; Seasonal is network-wide. */}
                {selectedCustomer &&
                  driverRow(
                    "base",
                    "Base",
                    `${selectedCustomer.name} daily base${pkgType !== "all" ? ` (${pkgType})` : ""}`,
                    () => fmtN(baseEff),
                    () => fmtN(baseEff)
                  )}
                {selectedCustomer &&
                  driverRow(
                    "growth",
                    "Growth ×",
                    "Compounded growth since onboarding",
                    (day) => growthFac(day).toFixed(2),
                    (p) =>
                      (p.days.length
                        ? p.days.reduce((s, d) => s + growthFac(d), 0) / p.days.length
                        : 1
                      ).toFixed(2)
                  )}
                {selectedCustomer &&
                  driverRow(
                    "alloc",
                    "Alloc %",
                    "Allocation to this hub (date-window aware)",
                    (day) => `${allocPctOf(day)}%`,
                    (p) => {
                      const v = new Set(p.days.map(allocPctOf));
                      return v.size === 1 ? `${[...v][0]}%` : "…";
                    }
                  )}
                {selectedCustomer &&
                  driverRow(
                    "dow",
                    "DOW ×",
                    "Per-hub day-of-week factor",
                    (day) => dowFacOf(day).toFixed(2),
                    () => "—"
                  )}
                {driverRow(
                  "seasonal",
                  "Seasonal ×",
                  "Active seasonal multiplier",
                  (day) => seasonalMultiplier(day).toFixed(2),
                  (p) => seasonalAvg(p.days).toFixed(2)
                )}

                {/* Hub events row */}
                <tr className="border-b-2 border-slate-300 bg-slate-50/60 text-[10px]">
                  <td className="sticky left-0 z-10 bg-slate-100 px-3 py-1 font-medium uppercase tracking-wide text-slate-400">
                    Events
                  </td>
                  {visiblePeriods.map((p) => {
                    const open = p.expandable && expanded.has(p.key);
                    const cells: ReactNode[] = [];
                    const mark = (days: string[], key: string) => {
                      const evs = eventsIn(days);
                      return (
                        <td
                          key={key}
                          className={`px-1 py-1 text-center ${key.endsWith("sub") ? "border-l border-slate-200" : ""}`}
                          title={evs
                            .map(
                              (e) =>
                                `${e.type} @ ${hubCode(e.hubId)}${
                                  e.hubId === hubId ? "" : " → here"
                                } ${fmtMD(e.startDate)}–${fmtMD(e.endDate)}`
                            )
                            .join("; ")}
                        >
                          {evs.length ? <span className="text-amber-600">⚠</span> : ""}
                        </td>
                      );
                    };
                    if (open) for (const day of p.days) cells.push(mark([day], `e-${p.key}-${day}`));
                    cells.push(mark(p.days, `e-${p.key}-sub`));
                    return <Fragment key={`e-${p.key}`}>{cells}</Fragment>;
                  })}
                  <td className="border-l-2 border-slate-300" />
                </tr>

                {/* TOTAL row */}
                <tr className="border-y-2 border-slate-300 bg-slate-100">
                  <td className="sticky left-0 z-10 bg-slate-100 px-3 py-2 font-semibold uppercase tracking-wide text-slate-700">
                    Total
                  </td>
                  {dataPeriodCells(totalByDate, "t", totalPeriod, true, (cur) =>
                    cur ? "bg-brand-100" : "bg-slate-100"
                  )}
                  {(() => {
                    const { f, a, rin, rout } = sumDays(totalByDate, allDays);
                    return (
                      <td className="border-l-2 border-slate-300 bg-slate-100 px-3 py-2 text-right text-[11px] font-semibold tabular-nums">
                        {subContent(f, a, rin, rout)}
                      </td>
                    );
                  })()}
                </tr>

                {/* Per-customer rows */}
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    className="group border-b border-slate-100 last:border-0 even:bg-slate-50/40 hover:bg-brand-50/30"
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium text-slate-900 group-even:bg-slate-50 group-hover:bg-brand-50">
                      {c.name}
                    </td>
                    {dataPeriodCells(c.byDate, c.id, rowPeriod.get(c.id) ?? [], false, (cur) =>
                      cur ? "bg-brand-50" : "bg-slate-50/60"
                    )}
                    {(() => {
                      const { f, a, rin, rout } = sumDays(c.byDate, allDays);
                      return (
                        <td className="border-l-2 border-slate-300 px-3 py-1.5 text-right text-[11px] font-medium tabular-nums">
                          {subContent(f, a, rin, rout)}
                        </td>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
